// ============================================================
// TARGET SPLIT - decompose a legacy `Target` into the roster's two
// halves: what combat owns (CombatState) and what the sheet owns
// (a Partial<DefenderSheet> of disagreements).
//
// Lives below both migration.ts and legacyAdapter.ts, which both need
// it - keeping it here is what stops those two importing each other.
// ============================================================
import type { Character, DefenderSheet } from '../character/types';
import type { Target } from '../../types';
import type { CombatState } from './types';
import { cachedDefenderSheet } from './projection';

/**
 * Tolerant array read. A Target can arrive from an imported session file
 * or a Firestore document and is NOT guaranteed well-formed - an import
 * fixture with `conditions: 0` is a real case in the E2E suite. This is
 * the roster's ingress, so it defends here rather than trusting callers,
 * the same discipline normalizeCharacter follows for stored characters.
 */
function asArray<T>(value: unknown): T[] {
  return Array.isArray(value) ? [...(value as T[])] : [];
}

// ────────────────────────────────────────────────────────────
// Target → the two halves
// ────────────────────────────────────────────────────────────

/** Everything combat owns, lifted straight off a legacy Target. */
/**
 * A target with the ground's own grants taken back out of it.
 *
 * `stateFromTarget` already refuses to store anything listed in
 * `derivedConditions` - but that field only survives if the writer kept
 * it. Most do not: `useApplyHits` and the turn countdown build a NEW
 * condition list out of the DERIVED target (`[...tgt.conditions]`, ground
 * grants included) and merge it into the raw one, which carries no
 * marker. The subtraction then had nothing to subtract, so attacking or
 * ending a turn while standing in a mire wrote the mire's conditions onto
 * the combatant permanently (v7.77.0).
 *
 * Applied at the single point where a tab becomes stored state, rather
 * than at each writer, because the next writer would forget too.
 *
 * A caveat inherent to the model: a condition the ground grants and one
 * the combatant genuinely acquires are the same string. If a man is set
 * alight in burning promethium, the fire leaves with the ground. That is
 * the same trade `stateFromTarget` has always made.
 */
export function withoutDerivedConditions(t: Target, derived: string[] | undefined): Target {
  if (!derived?.length || !t.conditions?.length) return t;
  const kept = t.conditions.filter((c) => !derived.includes(c));
  if (kept.length === t.conditions.length) return t;
  return { ...t, conditions: kept };
}

/**
 * The cover worth writing down.
 *
 * A 'derived' cover is what the MAP is saying for one attacker, and must
 * never be written down. IT USED TO BE DROPPED TO NOTHING, which restored
 * exactly what was there because the map only ever substituted when
 * nothing was pinned. Since v8.13.0 the better of the two wins, so a pin
 * CAN be outranked - and dropping to nothing would then destroy it. The
 * projection carries the displaced value for precisely this moment.
 */
function coverToStore(t: Target): CombatState['cover'] {
  const kept = t.cover?.source === 'derived' ? t.coverPinned : t.cover;
  return oneSpelling(kept ?? { level: 'none', ap: 0 });
}

/**
 * A stored cover is ALWAYS manual (a derived one is never stored), and an
 * absent `source` already means manual - so the stamp is dropped: one
 * spelling of one ruling. Two spellings had two screens re-writing a
 * combatant at each other for ever (v8.393.3, ./dev fuzz).
 */
function oneSpelling(cover: NonNullable<Target['cover']>): CombatState['cover'] {
  const { source: _manual, ...rest } = cover;
  return rest;
}

/**
 * The traits the ground lent, taken back out of a write (v8.310.9).
 *
 * `derivedTraits` was RECORDED on the projection and never subtracted:
 * `sheetFromTarget` carries `regeneration: t.regeneration ?? 0`, so the
 * first `updateTab` on anyone standing on lending ground - a countdown
 * tick, an applied hit - wrote the ground's Regeneration into their
 * overrides for good, and it stayed when they walked off. This is the
 * trait half of `withoutDerivedConditions`: a value that equals what the
 * ground lends, and differs from what the combatant had before this
 * write, is the ground's and goes back to the combatant's own.
 */
export function withoutDerivedTraits(
  updated: Target,
  before: Target,
  ground: Record<string, boolean | number> | undefined,
): Target {
  if (!ground) return updated;
  const u = updated as unknown as Record<string, unknown>;
  const b = before as unknown as Record<string, unknown>;
  // Only what the ground lent and nobody changed since: set to the lent
  // value now, and not already that value before.
  const lent = Object.entries(ground).filter(([field, value]) => value && u[field] === value && b[field] !== value);
  if (!lent.length) return updated;
  const out: Record<string, unknown> = { ...u };
  for (const [field] of lent) {
    if (field in b) out[field] = b[field];
    else delete out[field];
  }
  return out as unknown as Target;
}

export function stateFromTarget(t: Target, weaponAmmo: CombatState['weaponAmmo'] = {}): CombatState {
  return {
    rev: 0,
    woundsCurrent: t.wounds?.current ?? 0,
    hordeMagnitude: t.horde?.magnitude ?? 0,
    // MINUS anything the ground is granting. Those are derived from
    // where the combatant stands; writing them down would leave somebody
    // On Fire for ever after they walked out of the flames.
    conditions: asArray<string>(t.conditions).filter((c) => !(t.derivedConditions ?? []).includes(c)),
    criticalDamage: asArray<string>(t.criticalDamage),
    armour: { ...(t.armour ?? {}) },
    forceField: { ...(t.forceField ?? { active: false, protectionRating: 0, overloadThreshold: 0 }) },
    weaponAmmo,
    cover: coverToStore(t),
    roundCountdown: !!t.roundCountdown,
    countdownTickedThisRound: !!t.countdownTickedThisRound,
    defenderCustomMods: asArray(t.defenderCustomMods),
    defensiveStance: !!t.defensiveStance,
    frenzied: !!t.frenzied,
    vehicleFacing: t.vehicleFacing ?? null,
    // Conditionally: Firestore refuses a literal undefined (gotcha #2).
    ...(t.standingReaction ? { standingReaction: t.standingReaction } : {}),
    ...(t.activeStances?.length ? { activeStances: [...t.activeStances] } : {}),
    // Squad Mode rides back too (2026-10-23): a legacy write must not drop a brother out of it.
    ...(t.killTeam
      ? { killTeam: { ...t.killTeam, ...(t.killTeam.joined ? { joined: [...t.killTeam.joined] } : {}) } }
      : {}),
    // ...and what he has faced, or the same horror asks its Fear test again after any legacy write (2026-10-24).
    ...(t.dreadTested ? { dreadTested: { ...t.dreadTested } } : {}),
    ...(t.horde?.broken ? { hordeBroken: true } : {}),
  };
}

/**
 * The sheet values a legacy Target implies, for the keys a Target can
 * actually speak to. Keys it says nothing about (isFrenzied,
 * hasBattleRage) are omitted so the resolved sheet keeps its own answer.
 *
 * `armour` uses armourOriginal when present - that field existed
 * precisely to remember the pre-combat AP, which is the sheet value.
 */
export function sheetFromTarget(t: Target): Partial<DefenderSheet> {
  return {
    ...bonusesFromTarget(t),
    ...flagsFromTarget(t),
    machineAP: t.machineAP ?? 0,
    naturalArmour: t.naturalArmour ?? 0,
    daemonicProtection: t.daemonicProtection ?? 0,
    regeneration: t.regeneration ?? 0,
    armour: { ...(t.armourOriginal ?? t.armour ?? {}) },
    dodge: {
      canDodge: !!t.dodge?.canDodge,
      agility: t.dodge?.agility ?? 0,
      additionalDodges: t.dodge?.additionalDodges ?? 0,
    },
    parry: parryFromTarget(t),
    defenderWeapons: asArray(t.defenderWeapons),
    parryBaseSkill: t.parryBaseSkill ?? t.parry?.weaponSkill ?? 0,
    forceField: { ...(t.forceField ?? { active: false, protectionRating: 0, overloadThreshold: 0 }) },
    ...bookkeepingFromTarget(t),
    hardy: !!t.hardy,
    ironJaw: !!t.ironJaw,
    ironJawToughness: t.ironJawToughness ?? 0,
    counterAttackAvailable: !!t.counterAttackAvailable,
    allowFullDefenceChain: t.allowFullDefenceChain,
    hasDeflectShot: t.parry?.canParryRanged ?? t.canParryRanged ?? false,
    bodyType: t.bodyType ?? 'humanoid',
  };
}

/** The name, the characteristic bonuses and their unnatural multipliers. */
function bonusesFromTarget(t: Target) {
  return {
    name: t.name,
    toughnessBonus: t.toughnessBonus,
    strengthBonus: t.strengthBonus ?? 3,
    willpowerBonus: t.willpowerBonus ?? 3,
    unnaturalToughness: t.unnaturalToughness ?? 1,
    unnaturalAgility: t.unnaturalAgility ?? 1,
    unnaturalWeaponSkill: t.unnaturalWeaponSkill ?? 1,
    unnaturalStrength: t.unnaturalStrength ?? 1,
    unnaturalWillpower: t.unnaturalWillpower ?? 1,
  };
}

/** The trait flags a Target carries flat. */
function flagsFromTarget(t: Target) {
  return {
    trueGrit: !!t.trueGrit,
    stuffOfNightmares: !!t.stuffOfNightmares,
    astartes: !!t.astartes,
    fromBeyond: !!t.fromBeyond,
    blind: !!t.blind,
    incorporeal: !!t.incorporeal,
    undying: !!t.undying,
  };
}

function parryFromTarget(t: Target): DefenderSheet['parry'] {
  return {
    canParry: !!t.parry?.canParry,
    weaponSkill: t.parry?.weaponSkill ?? 0,
    hasPowerField: !!t.parry?.hasPowerField,
    additionalParries: t.parry?.additionalParries ?? 0,
    // Never emit an explicit undefined - Firestore rejects the document.
    ...(t.parry?.parryWeaponName !== undefined ? { parryWeaponName: t.parry.parryWeaponName } : {}),
    canParryRanged: t.parry?.canParryRanged ?? t.canParryRanged ?? false,
  };
}

/** Size, wounds, horde, fear, psy, xenos and the drawn token size. */
function bookkeepingFromTarget(t: Target) {
  return {
    size: t.size,
    woundsMax: t.wounds?.max ?? 0,
    isHorde: !!t.horde?.isHorde,
    hordeMaxMagnitude: t.horde?.maxMagnitude ?? 0,
    fearRating: t.fearRating ?? 0,
    psyRating: t.psyRating ?? 0,
    xenos: !!t.xenos,
    // Never emit undefined (Firestore); 0 means unset and IS worth
    // keeping - it is how an override clears a sheet's drawn size.
    ...(t.tokenDiameter !== undefined ? { tokenDiameter: t.tokenDiameter } : {}),
    ...(t.vehicleKind ? { vehicleKind: t.vehicleKind } : {}),
    ...(t.figure ? { figure: t.figure } : {}),
  };
}

/**
 * Keep only the keys where the Target disagrees with what the sheet
 * already resolves to. This is the whole trick: a pristine spawned
 * character migrates to `{}`, so nothing is frozen that could later
 * drift from its talents.
 */
export function diffOverrides(desired: Partial<DefenderSheet>, base: DefenderSheet): Partial<DefenderSheet> {
  const overrides: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(desired)) {
    // Undefined is not a disagreement, and Firestore cannot store it.
    if (value === undefined) continue;
    const current = (base as Record<string, unknown>)[key];
    if (JSON.stringify(value) !== JSON.stringify(current)) overrides[key] = value;
  }
  return overrides as Partial<DefenderSheet>;
}

const sameValue = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

/**
 * The overrides after a WRITE: only the keys the write actually changed
 * are re-judged against the sheet; every other key keeps the status it
 * had (pinned to its stored value, or not pinned at all).
 *
 * A plain `diffOverrides` of the written Target is wrong here, because
 * the Target a writer was handed is a PROJECTION: a psy rating lowered by
 * a `Dn:psy` drain would be read back as a disagreement with the sheet
 * and frozen as an override, then drained again on the next projection
 * (5, 3, 1, 0, and still 0 once the drain ended).
 *
 * @param prev     the overrides stored before the write
 * @param before   sheetFromTarget of the Target the writer was handed
 * @param after    sheetFromTarget of the Target the writer returned
 * @param base     what the sheet alone resolves to
 */
export function mergeEditedOverrides(
  prev: Partial<DefenderSheet>,
  before: Partial<DefenderSheet>,
  after: Partial<DefenderSheet>,
  base: DefenderSheet,
): Partial<DefenderSheet> {
  const stored = prev as Record<string, unknown>;
  const was = before as Record<string, unknown>;
  const resolved = base as unknown as Record<string, unknown>;
  // A key the write left alone KEEPS ITS STATUS, unjudged: its stored pin, or none. Only a changed
  // key is judged against the sheet. Re-judging the untouched ones would release a hand pin whenever
  // the sheet happens to resolve to the same value for a while - a stance that grants the one extra
  // dodge a GM had pinned, say - and the pin would not come back when the stance went down.
  // Undefined is not a disagreement, and Firestore cannot store it.
  const pinned = Object.entries(after)
    .map(([key, value]): [string, unknown] =>
      sameValue(was[key], value) ? [key, stored[key]] : [key, sameValue(value, resolved[key]) ? undefined : value],
    )
    .filter(([, value]) => value !== undefined);
  return Object.fromEntries(pinned) as Partial<DefenderSheet>;
}

/** Split a legacy Target into (sheet-overrides, combat state) against a given sheet. */
export function splitTarget(
  t: Target,
  sheetCharacter: Character,
  weaponAmmo: CombatState['weaponAmmo'] = {},
): { overrides: Partial<DefenderSheet>; state: CombatState } {
  return {
    overrides: diffOverrides(sheetFromTarget(t), cachedDefenderSheet(sheetCharacter)),
    state: stateFromTarget(t, weaponAmmo),
  };
}
